import React, { useEffect, useMemo, useRef, useState, } from 'react';
import Flatpickr from 'react-flatpickr';
import pl from 'flatpickr/dist/l10n/pl';
import it from 'flatpickr/dist/l10n/it';
import de from 'flatpickr/dist/l10n/de';
import cs from 'flatpickr/dist/l10n/cs';
import es from 'flatpickr/dist/l10n/es';
import en from 'flatpickr/dist/l10n/default';
import fr from 'flatpickr/dist/l10n/fr';
import nl from 'flatpickr/dist/l10n/nl';
import pt from 'flatpickr/dist/l10n/pt';
import ja from 'flatpickr/dist/l10n/ja';
import sk from 'flatpickr/dist/l10n/sk';
import gr from 'flatpickr/dist/l10n/gr';
import classNames from 'classnames';
import { IMaskInput } from 'react-imask';
import IMask from 'imask';
import { FormFieldContext } from '../FormField/FormFieldContext';
import { Icon } from '../Icon/Icon';
import uuidv4 from '../../utils/uuidv4';
import { containsLetters, buildStrictDate, getAcceptedInputFormats, parseByFormatTokens } from './Picker.utils';
/**
 * Additional inline styles for a disabled DatePicker button.
 * @type {{pointerEvents: string}}
 */
const disabledButtonStyle = { pointerEvents: 'none' };
/**
 * Regex to check against the native dateString format.
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/date
 * @type {RegExp}
 */
const NATIVE_INPUT_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Pads one-digit date numbers with a leading zero.
 * @param {number} d - The date number to pad with a leading zero.
 * @return {string} - Returns of the passed date number.
 * @example
 *    padDate(1) → '01'
 */
const padDate = (d) => `${d < 10 ? d.toString().padStart(2, '0') : d}`;
/**
 * Extract a separator from a given dateFormat.
 * @param {string} dateFormat - The given dateFormat to extract the separator.
 * @return {string} - The date separator, usually a one-char string.
 * @example
 *    getSeparator('Y-m-d') → '-'
 */
const getSeparator = (dateFormat) => dateFormat.substring(1, 2);
/**
 * Create an {@link IMask.MaskedDate.pattern} input pattern.
 * @param {string} dateFormat - The given dateFormat string.
 * @return {IMask.MaskedDate.pattern} - Return a masked date pattern.
 *                                      Note the backticks: They are part of it!.
 * @example
 *    createPattern('Y-m-d') → 'Y-`m-`d'
 */
const createPattern = (dateFormat) => {
    const separator = getSeparator(dateFormat);
    return dateFormat.replaceAll(separator, `${separator}\``);
};
/**
 * Given a dateFormat string return an object with dateFormat as keys and their index
 * by splitting it via separator.
 * @param dateFormat
 * @return {{[string]: number}}
 * @example
 *    getDateTokenPosition('Y-m-d') → {'Y': 0, 'm': 1, 'd': 2}
 */
const getDateTokenPosition = (dateFormat) => {
    const separator = getSeparator(dateFormat);
    return dateFormat.split(separator).reduce((prev, curr, idx) => ({
        ...prev,
        [curr]: idx,
    }), {});
};
/**
 * Get an object of placeholder characters mapped to the given dateFormat.
 * Each placeholder segment is reduced to a single character.
 *
 * @param {string} dateFormat - The given dateFormat.
 * @param {string} [placeholder] - The given placeholder string.
 * @return {{yearPlaceholderChar: string, dayPlaceholderChar: string, monthPlaceholderChar: string}}
 * @example
 *    getPlaceholderChars('Y/m/d', 'a/b/c') →
 *    { yearPlaceholderChar: 'a', monthPlaceholderChar: 'b', dayPlaceholderChar: 'c' }
 *
 *    getPlaceholderChars('d.m.Y', 'TT.MM.JJJJ') →
 *    { yearPlaceholderChar: 'J', monthPlaceholderChar: 'M', dayPlaceholderChar: 'T' }
 */
const getPlaceholderChars = (dateFormat, placeholder) => {
    const separator = getSeparator(dateFormat);
    const { Y: yearPos, m: monthPos, d: dayPos } = getDateTokenPosition(dateFormat);
    const getSinglePlaceholderChar = (value, fallback) => {
        const normalized = value?.trim();
        return normalized ? normalized.charAt(0) : fallback;
    };
    if (placeholder) {
        const splittedPlaceholder = placeholder.split(separator);
        return {
            yearPlaceholderChar: getSinglePlaceholderChar(splittedPlaceholder[yearPos], 'Y'),
            monthPlaceholderChar: getSinglePlaceholderChar(splittedPlaceholder[monthPos], 'm'),
            dayPlaceholderChar: getSinglePlaceholderChar(splittedPlaceholder[dayPos], 'd'),
        };
    }
    return {
        yearPlaceholderChar: 'Y',
        monthPlaceholderChar: 'm',
        dayPlaceholderChar: 'd',
    };
};
/**
 * The heart of date parsing. Is used by {@link Flatpickr} and also {@link IMask}
 * to parse the date input from a string to a valid date.
 *
 * The parser is tolerant towards different input formats to support:
 * - manual input
 * - pasted values
 * - browser autofill
 *
 * It first attempts to parse native HTML5 date strings (`yyyy-mm-dd`),
 * then tries the configured {@link dateFormat} and a set of supported
 * fallback formats (e.g. different separators or token orders).
 *
 * The parsing is strict and validates that the resulting date does not overflow
 * (e.g. `31.02.2020` will be rejected).
 *
 * If no valid date can be parsed, the function returns `null` (or an empty string
 * for placeholder-like values).
 *
 * @param {string} separator - The current date separator.
 * @param {number} yearPos - The position of the year in the date array.
 * @param {number} monthPos - The position of the month in the date array.
 * @param {number} dayPos - The position of the day in the date array.
 * @param {boolean} isNativeInput - If the input is a native or non-native input.
 * @param {string} dateFormat - The configured dateFormat.
 * @return {(date: string) => (string | null | Date)} - Returns a partially-applied function to parse dates.
 * @example
 *    parseDate('.', 2, 1, 0, false, 'd.m.Y')('1990-01-01') → Date
 *    parseDate('.', 2, 1, 0, false, 'd.m.Y')('01/01/1990') → Date
 */
export const parseDate = (separator, yearPos, monthPos, dayPos, isNativeInput, dateFormat) => (date) => {
    if (!date?.trim()) {
        return '';
    }
    /**
     * Placeholder / mask chars should not be parsed.
     */
    if (containsLetters(date)) {
        return '';
    }
    const trimmedDate = date.trim();
    /**
     * Native HTML date format / common browser autofill format
     * Example: 1990-01-01
     */
    if (NATIVE_INPUT_DATE_REGEX.test(trimmedDate)) {
        const [year, month, day] = trimmedDate.split('-').map(Number);
        const parsedNative = buildStrictDate({ year, month, day });
        return parsedNative;
    }
    /**
     * Try configured format first and then selected tolerant fallbacks
     * Example for configured d.m.Y:
     *    - 01.01.1990
     *    - 01-01-1990
     *    - 01/01/1990
     *    - 1990-01-01
     */
    const acceptedFormats = getAcceptedInputFormats(dateFormat);
    for (const acceptedFormat of acceptedFormats) {
        const parsed = parseByFormatTokens(trimmedDate, acceptedFormat);
        if (parsed) {
            return parsed;
        }
    }
    /**
     * Legacy fallback:
     *    keep current behavior for same token order + different separator
     *    in case some custom format depends on it.
     */
    let normalizedDate = trimmedDate;
    if (!normalizedDate.includes(separator)) {
        normalizedDate = normalizedDate.replace(/[/.-]/g, separator);
    }
    if (normalizedDate.includes(separator)) {
        const yearMonthDay = normalizedDate.split(separator);
        if (yearMonthDay[yearPos] && yearMonthDay[monthPos] && yearMonthDay[dayPos]) {
            const fallbackParsed = buildStrictDate({
                year: Number(yearMonthDay[yearPos]),
                month: Number(yearMonthDay[monthPos]),
                day: Number(yearMonthDay[dayPos]),
            });
            if (fallbackParsed) {
                return fallbackParsed;
            }
        }
    }
    /**
     * Check if the date origins from a non-native datepicker.
     */
    if (!isNativeInput) {
        console.error(`Bronson-React Picker: Your date (${date}) is invalid. Please check that it matches the dateFormat (${dateFormat}) or a supported alternative format.`);
        return null;
    }
    return null;
};
/**
 * Given a dateFormat, the partially applied function returns
 * an {@link IMask} date to string formatter function.
 * Only used when {@link maskedInput} is set to `true`.
 * @see https://imask.js.org/api/#maskedformat
 *
 * @param {string} dateFormat - The currently used {@link dateFormat}.
 * @return {function(*): string} - {@link IMask} date to string formatter function.
 * @example
 *    formatDate('Y-m-d')(1-12-2000) → '2000-12-01'
 */
const formatDate = (dateFormat) => (date) => {
    const day = padDate(date.getDate());
    const month = padDate(date.getMonth() + 1);
    const year = date.getFullYear();
    const { Y: yearPos, m: monthPos, d: dayPos } = getDateTokenPosition(dateFormat);
    const separator = getSeparator(dateFormat);
    const newDate = [];
    newDate[yearPos] = year;
    newDate[monthPos] = month;
    newDate[dayPos] = day;
    return newDate.join(separator);
};
/**
 * Set the correct locale for the calendar.
 * @param {('pl'|'de'|'en'|'it'|'cs'|'es'|'fr'|'nl'|'pt'|'ja'|'sk'|'el'|'gr')} locale - The locale to set.
 * @return {CustomLocale|null}
 * @example
 *    setLocale('dk') → null
 */
const setLocale = (locale) => {
    switch (locale) {
        case 'pl':
            return pl.pl;
        case 'de':
            return de.de;
        case 'it':
            return it.it;
        case 'cs':
            return cs.cs;
        case 'es':
            return es.es;
        case 'en':
            return en;
        case 'fr':
            return fr.fr;
        case 'nl':
            return nl.nl;
        case 'pt':
            return pt.pt;
        case 'ja':
            return ja.ja;
        case 'sk':
            return sk.sk;
        case 'el':
        case 'gr':
            return gr.gr;
        default:
            return null;
    }
};
/**
 * Checks if the given variable is a valide date.
 * @param {*} v - The variable to check.
 * @return {boolean}
 * @example
 *    isValidDate(new Date('2000-01-01')) → true
 *    isValidDate(new Date('21')) → false
 *    isValidDate('2000-01-01') → false
 */
const isValidDateObject = (v) => v instanceof Date && !Number.isNaN(v.getDate());
/**
 * Naive check if the date matches the passed {@link dateFormat}.
 * @param {string} date
 * @param {string} dateFormat
 * @returns {boolean}
 * @example
 *    matchesDateFormat('2000-01-01', 'd.m.Y') → false
 *    matchesDateFormat('2000-01-01', 'Y.m.d') → false
 *    matchesDateFormat('2000-01-01', 'Y-m-d') → true
 */
export const matchesDateFormat = (date, dateFormat) => {
    /**
     * If no date was specified or if it contains letters (re iMask chars)
     * the date does not match.
     */
    if (!date || date.search(/[a-zA-Z]/g) >= 0) {
        return false;
    }
    // @ts-ignore @TODO: Fix to comply with expected type.
    const dateObj = globalThis?.flatpickr?.parseDate(date, dateFormat);
    const day = padDate(dateObj.getDate());
    const month = padDate(dateObj.getMonth() + 1);
    const year = dateObj.getFullYear().toString();
    const { Y: yearPos, m: monthPos, d: dayPos } = getDateTokenPosition(dateFormat);
    const separator = getSeparator(dateFormat);
    if (!date.includes(separator)) {
        return false;
    }
    const splitDate = date.split(separator);
    const hasDay = dayPos ? splitDate[dayPos] === day : true;
    const hasMonth = monthPos ? splitDate[monthPos] === month : true;
    const hasYear = yearPos ? splitDate[yearPos] === year : true;
    return hasDay && hasMonth && hasYear;
};
/**
 * Bronson Picker component.
 * Generic date and time picker component
 * to enable {@link DatePicker} and {@link TimePicker}.
 * @internal
 * @constructor
 */
export function Picker({ ariaLabel = 'toggle picker', className, componentClassName, dateFormat = 'Y-m-d', disabled = false, disableDays = [], error = false, flatpickrOptions = {}, flatpickrProps, id, inputProps, locale = 'de', maskedInput = true, mode = 'single', monthSelectorDropdown = false, name, noBackground = true, onBlur, onChange, onError, pickerClassName, placeholder, value: customValue, weekNumbers, testId, required, }) {
    /**
     * Generate unique id, if prop `id` is not provided to improve accessibility.
     */
    const pickerId = useMemo(() => id ?? uuidv4('picker-'), [id]);
    /**
     * Track internal state via the wrapping {@link FormFieldContext}.
     * @type {object}
     */
    const context = React.useContext(FormFieldContext);
    /**
     * Hook to set active/inactive state on the Picker.
     */
    useEffect(() => {
        if (context?.setElementActive) {
            context.setElementActive(!!customValue);
        }
    }, [customValue]);
    /**
     * Ref to the React flatpickr node.
     * @type {React.MutableRefObject<null>}
     */
    const flatpickrRef = useRef(null);
    /**
     * Ref to the masked input field.
     * Only utilized when {@link maskedInput} is set to `true`.
     * @type {React.MutableRefObject<null>}
     */
    const maskRef = useRef(null);
    /**
     * Track the internal date state.
     */
    const [date, setDate] = useState(customValue ?? '');
    /**
     * Track error states.
     */
    const [hasError, setHasError] = useState(false);
    /**
     * Track whether native/non-native input is used.
     * Is set via the {@link flatpickrRef}.
     */
    const [isMobileInput, setIsMobileInput] = useState(false);
    /**
     * Track the current {@link IMask.MaskedPattern.lazy} state.
     * @see https://imask.js.org/api/#maskedpatternlazy
     */
    const [isLazy, setIsLazy] = useState(true);
    /**
     * Memoize the instance’s {@link dateFormat} separator string, e.g. '-' or '/' etc..
     * @type {string}
     */
    const separator = useMemo(() => getSeparator(dateFormat), [dateFormat]);
    /**
     * Memoize the instance’s date part positions and extract them into variables.
     * @type {object}
     */
    const { Y: yearPos, m: monthPos, d: dayPos } = useMemo(() => getDateTokenPosition(dateFormat), [dateFormat]);
    /**
     * Memoize the instance’s date part placeholder and extract them into variables.
     * @type {object}
     */
    const { yearPlaceholderChar, monthPlaceholderChar, dayPlaceholderChar } = useMemo(() => getPlaceholderChars(dateFormat, placeholder), [dateFormat, placeholder]);
    /**
     * Memoize the instance’s date parser.
     */
    const mParseDate = useMemo(() => parseDate(separator, yearPos, monthPos, dayPos, isMobileInput, dateFormat), [separator, yearPos, monthPos, dayPos, isMobileInput, dateFormat]);
    /**
     * Memoize the instance’s date formatter.
     * @type {function(*): string}
     */
    const mFormatDate = useMemo(() => formatDate(dateFormat), [dateFormat]);
    /**
     * Set the current internal date state via a side effect.
     */
    useEffect(() => {
        setDate(customValue ?? '');
    }, [customValue]);
    /**
     * Set the current internal native/non-native input mode via a side effect.
     */
    useEffect(() => {
        setIsMobileInput(flatpickrRef?.current?.flatpickr?.isMobile);
    }, [flatpickrRef]);
    /**
     * Fixes a bug where the mobile input won’t reflect the state changes.
     * We just fix this for `[disabled]` here.
     * @see https://github.com/haoxins/react-flatpickr/issues/27
     * @see https://github.com/haoxins/react-flatpickr/issues/125
     */
    useEffect(() => {
        if (isMobileInput) {
            disabled
                ? flatpickrRef?.current?.flatpickr?.mobileInput?.setAttribute('disabled', '')
                : flatpickrRef?.current?.flatpickr?.mobileInput?.removeAttribute('disabled');
        }
    }, [disabled, isMobileInput]);
    /**
     * Ref to the Flatpickr wrapping `<div>` element.
     */
    const wrapperDivRef = useRef(null);
    /**
     * Holds the target element where the Flatpickr calendar should be appended to if inside a `<dialog>`.
     */
    const [appendToElement, setAppendToElement] = useState(null);
    /**
     * Key to track if Flatpickr instance is rendered in a `<dialog>`.
     * If so, the keys triggers a re-render of the component after setting the 'appendToElement'.
     */
    const key = appendToElement ? 'with-append' : 'no-append';
    /**
     * Check if the Flatpickr instance is rendered in a `<dialog>` and if so, setting the 'appendToElement'.
     */
    useEffect(() => {
        if (wrapperDivRef.current?.closest('dialog')) {
            setAppendToElement(wrapperDivRef.current);
        }
    }, []);
    const componentPickerWrapperClass = classNames('c-input', className).trim();
    const componentPickerClass = classNames('c-input__input', pickerClassName).trim();
    const componentPickerButtonClass = classNames('c-input__addon', {
        'c-input__addon--no-background': noBackground,
    }).trim();
    const componentPickerButtonIconClass = classNames('c-icon', `c-icon--[semantic-${flatpickrOptions?.enableTime ? 'clock' : 'calendar'}]`).trim();
    /**
     * Normalize a Flatpickr hook into an array.
     * Flatpickr allows hooks to be defined as a single function or an array of functions.
     * Ensures consistent handling by always returning an array.
     */
    const normalizeFlatpickrHook = (hook) => {
        if (!hook)
            return [];
        return Array.isArray(hook) ? hook : [hook];
    };
    /**
     * Execute one or multiple Flatpickr hooks with the provided arguments.
     */
    const runFlatpickrHooks = (hooks, args) => {
        normalizeFlatpickrHook(hooks).forEach((hook) => {
            if (typeof hook === 'function') {
                hook(...args);
            }
        });
    };
    /**
     * Apply layout adjustments when the Picker is rendered inside a `<dialog>` (Modal).
     *
     * By default, Flatpickr positions the calendar absolutely relative to the viewport,
     * which causes clipping and layering issues inside dialogs. When the Picker is
     * detected inside a dialog (via {@link appendToElement}), the calendar is instead
     * rendered as part of the normal document flow.
     *
     * On small viewports, the calendar is additionally scaled down to prevent layout
     * overflow while preserving its internal grid structure.
     *
     * @param {*} instance - Flatpickr instance.
     */
    const applyDialogCalendarStyles = (instance) => {
        if (!appendToElement || !instance?.calendarContainer) {
            return;
        }
        const calendarContainer = instance.calendarContainer;
        const isMobileViewport = window.matchMedia('(max-width: 575px)').matches;
        calendarContainer.style.position = 'static';
        calendarContainer.style.top = 'auto';
        calendarContainer.style.left = 'auto';
        calendarContainer.style.right = 'auto';
        calendarContainer.style.bottom = 'auto';
        calendarContainer.style.inset = 'auto';
        calendarContainer.style.marginTop = '8px';
        if (isMobileViewport) {
            calendarContainer.style.transform = 'scale(0.9)';
            calendarContainer.style.transformOrigin = 'top left';
            calendarContainer.style.width = 'max-content';
        }
        else {
            calendarContainer.style.transform = '';
            calendarContainer.style.width = '';
        }
    };
    /**
     * Handle pasted dates before IMask distributes the raw characters
     * into the currently configured date pattern.
     */
    const onPasteDate = (value) => {
        const parsedDate = mParseDate(value);
        if (!(parsedDate instanceof Date) || !isValidDateObject(parsedDate)) {
            return false;
        }
        const formattedDate = mFormatDate(parsedDate);
        setDate(formattedDate);
        setIsLazy(false);
        if (maskRef?.current?.maskRef?.updateValue) {
            maskRef.current.maskRef.updateValue();
        }
        if (flatpickrRef?.current?.flatpickr) {
            flatpickrRef.current.flatpickr.setDate(parsedDate, false);
        }
        onChange?.(formattedDate);
        context?.setElementActive?.(true);
        return true;
    };
    /**
     * Internal onValue function to handle date changes
     * and update the internal state.
     */
    const onValue = (value) => {
        /**
         * Set the lazy state to false and show the IMask if applicable.
         * Only used when {@link maskedInput} is set to `true`.
         */
        setIsLazy(false);
        /**
         * If we have a value we set the internal {@link date} state to it and call any external `onChange` function
         */
        if (value) {
            setDate(value);
        }
        /**
         * Synchronize the current {@link IMask.MaskedDate.value} from the input view.
         * Only used when {@link maskedInput} is set to `true`.
         * @see https://imask.js.org/api/#inputmaskupdatevalue
         */
        if (maskRef?.current?.maskRef?.updateValue) {
            maskRef?.current.maskRef.updateValue();
        }
        return value;
    };
    /**
     * Display a masked input via {@link IMask} when {@link maskedInput} is set to `true`
     * otherwise use a simple input element. For {@link TimePicker} we always disable inputMasks.
     * @return {JSX.Element}
     */
    const renderInput = () => maskedInput && mode === 'single' && !flatpickrOptions?.enableTime ? (React.createElement(IMaskInput, { className: componentPickerClass, "aria-invalid": error ? true : undefined, name: name, ref: maskRef, "data-input": true, "data-testid": testId, mask: Date, overwrite: true, pattern: createPattern(dateFormat), format: mFormatDate, 
        // @ts-ignore @FIXME: Consolidate to `(value: string) => Date`.
        parse: mParseDate, blocks: {
            d: { mask: IMask.MaskedRange, placeholderChar: dayPlaceholderChar, from: 1, to: 31, maxLength: 2 },
            m: { mask: IMask.MaskedRange, placeholderChar: monthPlaceholderChar, from: 1, to: 12, maxLength: 2 },
            Y: { mask: IMask.MaskedRange, placeholderChar: yearPlaceholderChar, from: 1, to: 2999 },
        }, autofix: false, lazy: isLazy, disabled: disabled, id: pickerId, required: required, onPaste: (event) => {
            const pastedValue = event.clipboardData.getData('text');
            if (pastedValue && onPasteDate(pastedValue)) {
                event.preventDefault();
            }
        }, ...inputProps })) : (React.createElement("input", { "data-testid": testId, "aria-invalid": error ? true : undefined, name: name, className: componentPickerClass, placeholder: placeholder, disabled: disabled, "data-input": true, id: pickerId, required: required, ...inputProps }));
    /**
     * Render the DatePicker calendar or TimePicker clock icon.
     * @return {JSX.Element}
     */
    const renderIcon = () => {
        return React.createElement(Icon, { className: componentPickerButtonIconClass, viaCss: true });
    };
    /**
     * Render the DatePicker calendar or TimePicker clock button.
     * @return {JSX.Element}
     */
    const renderButton = () => {
        return (React.createElement("button", { className: componentPickerButtonClass, type: "button", tabIndex: -1, "aria-label": ariaLabel, "data-toggle": true, style: disabled ? disabledButtonStyle : undefined }, renderIcon()));
    };
    const { onOpen: flatpickrOnOpen, onReady: flatpickrOnReady, ...flatpickrOptionsRest } = flatpickrOptions;
    return (React.createElement("div", { className: componentClassName, ref: wrapperDivRef },
        React.createElement(Flatpickr, { className: componentPickerWrapperClass, ref: flatpickrRef, key: key, ...flatpickrProps, value: date, 
            /**
             * Hook
             * @see node_modules/flatpickr/dist/typings.d.ts
             */
            onChange: (dates, currentDateString) => {
                if (!hasError) {
                    const dateString = onValue(currentDateString);
                    if (dateString) {
                        onChange?.(dateString);
                    }
                    /**
                     * Handle active/inactive state on the Picker.
                     */
                    context?.setElementActive?.(!!dateString);
                }
            }, 
            /**
             * The `onBlur` hook gets the current value from the input,
             * as {@link Flatpickr} does not support `blur` hooks itself.
             */
            onBlur: (event) => {
                if (!hasError) {
                    /**
                     * Prevent blurs when the calendar popup is still open.
                     */
                    if (!flatpickrRef?.current?.flatpickr?.isOpen) {
                        const inputValue = event.target.value;
                        const dateString = onValue(inputValue);
                        /**
                         * Check if the input is a native (mobile) HTML5 input.
                         * If so, we need to convert the W3C standard date format
                         * to the one that was passed for {@link dateFormat}.
                         */
                        if (isMobileInput) {
                            const nativeDate = new Date(dateString);
                            if (isValidDateObject(nativeDate)) {
                                /**
                                 * Format from `YYYY-MM-DD` to {@link dateFormat}.
                                 */
                                const formattedDate = flatpickrRef?.current?.flatpickr?.formatDate(nativeDate, dateFormat);
                                onBlur?.(formattedDate);
                            }
                            else {
                                onError?.(`Bronson-React Picker: Native date picker provided an invalid date (${dateString})`);
                            }
                        }
                        else {
                            onBlur?.(dateString);
                        }
                        setIsLazy(true);
                    }
                }
            }, onValueUpdate: () => {
                if (maskRef?.current?.maskRef?.updateValue) {
                    maskRef?.current.maskRef.updateValue();
                }
            }, onClick: () => setIsLazy(false), options: {
                /**
                 * Callback for {@link Flatpickr} internal error handler.
                 * @param {Error} err - Internal Flatpickr error.
                 */
                errorHandler: (err) => {
                    /**
                     * Check if the error has a message.
                     * Set the Picker {@link hasError} state to `true`. This prevents
                     * other callback from fire due to the async value reset further down.
                     * Only recover for date inputs but not when {@link flatpickrOptions?.enableTime}
                     * is set.
                     */
                    if (err?.message && !flatpickrOptions?.enableTime) {
                        /**
                         * Prevent Flatpickr from throwing an error when the initial mask is displayed.
                         */
                        if (maskRef.current?.maskRef?.masked?.value?.includes(yearPlaceholderChar) &&
                            maskRef.current?.maskRef?.masked?.value?.includes(monthPlaceholderChar) &&
                            maskRef.current?.maskRef?.masked?.value?.includes(dayPlaceholderChar)) {
                            setHasError(true);
                            setTimeout(() => {
                                setHasError(false);
                            }, 0);
                            if (!hasError) {
                                onChange?.('');
                            }
                            return;
                        }
                        /**
                         * Call the {@link onError} callback.
                         */
                        onError?.(err?.message);
                        /**
                         * IF a Flatpickr instance is present, try to recover the last valid value.
                         */
                        if (flatpickrRef?.current?.flatpickr) {
                            setHasError(true);
                            setTimeout(() => {
                                /**
                                 * Only recover valid values, otherwise just ignore.
                                 */
                                if (matchesDateFormat(date, dateFormat)) {
                                    if (maskRef.current?.maskRef?.updateValue) {
                                        maskRef.current.maskRef.updateValue();
                                    }
                                    flatpickrRef?.current?.flatpickr?.setDate(date);
                                }
                                else if (mode === 'range' || mode === 'multiple') {
                                    flatpickrRef?.current?.flatpickr?.setDate(date);
                                }
                                setHasError(false);
                            }, 0);
                        }
                    }
                },
                wrap: true,
                allowInput: true,
                mode,
                locale: setLocale(locale),
                /**
                 * Disable custom date parsing for {@link TimePicker}.
                 * @param dateStr
                 * @return {null|string|Date}
                 */
                parseDate: flatpickrOptions?.enableTime ? null : (dateStr) => mParseDate(dateStr),
                dateFormat,
                disable: disableDays,
                disableMobile: true,
                nextArrow: '<i class="c-icon c-icon--[semantic-forward]" aria-hidden="true"></i>',
                prevArrow: '<i class="c-icon c-icon--[semantic-back]" aria-hidden="true"></i>',
                monthSelectorType: monthSelectorDropdown ? 'dropdown' : 'static',
                weekNumbers,
                ...(appendToElement ? { appendTo: appendToElement } : {}),
                onReady: [
                    (selectedDates, currentDateString, instance) => {
                        applyDialogCalendarStyles(instance);
                        runFlatpickrHooks(flatpickrOnReady, [selectedDates, currentDateString, instance]);
                    },
                ],
                onOpen: [
                    (selectedDates, currentDateString, instance) => {
                        applyDialogCalendarStyles(instance);
                        runFlatpickrHooks(flatpickrOnOpen, [selectedDates, currentDateString, instance]);
                    },
                ],
                ...flatpickrOptionsRest,
            } },
            renderInput(),
            renderButton())));
}
